// userOrgs.js — load every organization the signed-in user belongs to.
//
// Powers the mode switcher in UserMenu. Returns the user's primary
// owned org (from profile.merchantOrgId) PLUS any orgs they're an
// active staff member of (from the collection-group query on
// staff/{uid}). Each entry carries { orgId, name, role, isPrimaryOwner,
// type } so the switcher can show role badges + brand identity.
//
// Permissions: the staff/{uid} read is allowed by firestore.rules' staff
// match block (self-read). The orgs/{orgId} read for the brand name is
// public — every org doc is publicly readable for storefront purposes.
//
// Caching: 5-minute in-memory TTL keyed by uid. Invalidated by
// invalidateUserOrgsCache() — called from any flow that mutates an
// org membership (org create, staff invite accept, leave, etc.).

import { useEffect, useState } from 'react'
import { collection, collectionGroup, doc, getDoc, getDocs, query, where } from 'firebase/firestore'
import { db } from '../firebase.js'

const TTL_MS = 5 * 60 * 1000
const _cache = new Map()  // uid → { orgs, expiresAt }
let _subscribers = []
// Page-session dedupe flag for the permission-denied debug log
// (QA flagged the repeated DEBUG noise 2026-05-17).
let _userOrgsDeniedLogged = false

export function invalidateUserOrgsCache(uid = null) {
  if (uid) _cache.delete(uid)
  else _cache.clear()
  for (const fn of _subscribers) { try { fn() } catch (_) {} }
}

/**
 * Returns the full set of orgs the user belongs to.
 *
 * @returns {Promise<Array<{ orgId, name, slug, type, role, isPrimaryOwner, isActive }>>}
 */
export async function loadUserOrgs(user) {
  if (!user?.uid) return { orgs: [], accountType: 'personal', staffQueryFailed: false }
  const cached = _cache.get(user.uid)
  if (cached && Date.now() < cached.expiresAt) {
    return { orgs: cached.orgs, accountType: cached.accountType, staffQueryFailed: cached.staffQueryFailed }
  }

  // Two parallel reads:
  //   1. profile.merchantOrgId — the user's primary owned org if they
  //      created one via /organization.
  //   2. collectionGroup staff where uid == me — every staff doc the
  //      user has anywhere in the platform.
  let primaryOrgId = null
  try {
    const profSnap = await getDoc(doc(db, 'profiles', user.uid))
    primaryOrgId = profSnap.exists() ? (profSnap.data().merchantOrgId || null) : null
  } catch { /* fall through */ }

  let staffDocs = []
  let staffQueryFailed = false
  try {
    const q = query(
      collectionGroup(db, 'staff'),
      where('uid', '==', user.uid),
      where('status', '==', 'active'),
    )
    const snap = await getDocs(q)
    staffDocs = snap.docs.map(d => ({ ...d.data(), _ref: d.ref }))
    console.debug('[userOrgs] staff query', { uid: user.uid, found: staffDocs.length })
  } catch (err) {
    // The collectionGroup('staff') WHERE uid==me query is sensitive to
    // Firestore's rule analyzer behavior. For collection-group queries
    // the analyzer must prove the rule from the query's where filters
    // alone — it can't symbolically tie the literal `user.uid` we
    // passed to `request.auth.uid`. Even with a resource.data.uid
    // predicate in the rules, the analyzer's behavior with the OR
    // disjunction in the merchant staff rule isn't reliable.
    //
    // Functional impact is bounded:
    //   - Primary org still loads (resolved via profile.merchantOrgId)
    //   - Only multi-shop staff inheritance is impacted — the
    //     mode-switcher won't show shops the user is on staff at
    //     other than their primary
    //
    // Log at warn for permission-denied (expected) and error for
    // everything else (real bugs). Either way, the staffQueryFailed
    // flag drives the UI fallback so the merchant chrome stays usable.
    //
    // PROPER FIX (next session): denormalize a `userShops/{uid}/orgs/
    // {orgId}` index doc when staff is created/updated. Self-owned
    // path → simple read rule → no collection-group needed. Heavier
    // schema change so it's punted; this catch is the bridge.
    // Permission-denied here is a known-acceptable outcome of the rules-
    // analyzer's behavior on collection-group disjunctions — it's not a
    // production bug, it's the limitation we're working around with the
    // staffQueryFailed fallback. Demoted from warn to debug so it stops
    // showing up in users' DevTools by default. Anything OTHER than
    // permission-denied is a real failure and stays at error level.
    //
    // The proper fix (denormalize userShops/{uid}/orgs/{orgId} so a
    // simple single-doc read replaces this query) is tracked separately
    // and would let us drop this whole try/catch.
    if (err?.code === 'permission-denied') {
      // Dedupe per page session — QA flagged the repeated noise
      // (every page load fires this twice).
      if (!_userOrgsDeniedLogged) {
        console.debug('[userOrgs] staff collection-group denied — multi-shop inheritance disabled, primary org loads via profile.merchantOrgId')
        _userOrgsDeniedLogged = true
      }
    } else {
      console.error('[userOrgs] staff collectionGroup query failed:', err)
    }
    staffQueryFailed = true
  }

  // Read accountType so callers can distinguish "this user is a
  // merchant who hasn't created their org yet" from "this user is a
  // pure consumer." Returned alongside orgs for UI gating decisions.
  let accountType = 'personal'
  try {
    const profSnap = await getDoc(doc(db, 'profiles', user.uid))
    if (profSnap.exists()) accountType = profSnap.data().accountType || 'personal'
  } catch { /* fall through */ }

  // Staff-fallback path. When the collectionGroup('staff') query fails
  // for non-owner users (the known rules-analyzer disjunction issue)
  // we still want the UserMenu mode switcher to show their shop. Fall
  // back to walking the merchant_staff connections — same data the
  // Preferences subscription view uses for staff-tier inheritance.
  //
  // Only run when the collectionGroup query produced nothing AND the
  // user isn't already the primary owner of an org (no need to
  // duplicate-discover their own org). Lazy import to avoid circular
  // deps at module load (staffStatus → entitlements → ...). Pre-fix,
  // staff/admin users signed in and saw a consumer-only menu with no
  // way to reach their shop's dashboard.
  if (staffDocs.length === 0 && !primaryOrgId) {
    try {
      const { loadStaffStatus } = await import('./staffStatus.js')
      const s = await loadStaffStatus(user.uid)
      // Two-path resolution: new schema already carries the orgId on
      // the staff doc itself; legacy connections require an extra hop
      // through the owner's profile.merchantOrgId. loadStaffStatus
      // tries the new schema first and falls back to legacy.
      let resolvedOrgId = null
      if (s?.orgId) {
        resolvedOrgId = s.orgId
      } else if (s?.shopOwnerUid) {
        const ownerSnap = await getDoc(doc(db, 'profiles', s.shopOwnerUid))
        resolvedOrgId = ownerSnap.exists() ? (ownerSnap.data().merchantOrgId || null) : null
      }
      if (resolvedOrgId) {
        // Synthesize a staff doc shape that the org-display logic
        // below can consume — keeps the rest of this function
        // unchanged. role: 'admin' if Apple-Developer-style admin,
        // 'staff' otherwise; staffStatus already resolves that.
        staffDocs.push({
          orgId:  resolvedOrgId,
          role:   s.staffRole || 'staff',
          status: 'active',
          uid:    user.uid,
        })
        // Mark as no-longer-failed since we found the org via the
        // alternate path — the UI shouldn't show the
        // "couldn't load org data" banner anymore.
        staffQueryFailed = false
      }
    } catch (err) {
      console.warn('[userOrgs] staff fallback lookup failed:', err?.message)
    }
  }

  // Build a unique orgId set — the primary org may also have a staff
  // doc (it always does post-create), so dedupe.
  const orgIds = new Set()
  if (primaryOrgId) orgIds.add(primaryOrgId)
  for (const s of staffDocs) {
    if (s.orgId) orgIds.add(s.orgId)
  }

  // Fetch each org doc for display (name, slug, type). Public-readable
  // so no permission risk.
  const orgs = await Promise.all(Array.from(orgIds).map(async orgId => {
    const staff = staffDocs.find(s => s.orgId === orgId)
    let name = orgId, slug = '', type = 'shop'
    try {
      const orgSnap = await getDoc(doc(db, 'organizations', orgId))
      if (orgSnap.exists()) {
        const d = orgSnap.data()
        name = d.name || orgId
        slug = d.slug || ''
        type = d.type || 'shop'
      }
    } catch { /* fall through */ }
    return {
      orgId,
      name,
      slug,
      type,
      role:           staff?.role || 'owner',  // primary org without staff doc → owner
      isPrimaryOwner: orgId === primaryOrgId,
      isActive:       (staff?.status ?? 'active') === 'active',
    }
  }))

  // Sort: primary owner first, then by role hierarchy, then alphabetical.
  const ROLE_ORDER = { owner: 0, manager: 1, sommelier: 2, staff: 3, viewer: 4 }
  orgs.sort((a, b) => {
    if (a.isPrimaryOwner !== b.isPrimaryOwner) return a.isPrimaryOwner ? -1 : 1
    const ra = ROLE_ORDER[a.role] ?? 9
    const rb = ROLE_ORDER[b.role] ?? 9
    if (ra !== rb) return ra - rb
    return (a.name || '').localeCompare(b.name || '')
  })

  const result = { orgs, accountType, staffQueryFailed }
  _cache.set(user.uid, { ...result, expiresAt: Date.now() + TTL_MS })
  return result
}

/**
 * React hook wrapper. Refetches on user change + on cache-invalidate.
 */
export function useUserOrgs(user) {
  const [state, setState] = useState({ orgs: [], accountType: 'personal', staffQueryFailed: false })
  const [loading, setLoading] = useState(true)
  const [bumpKey, setBumpKey] = useState(0)

  useEffect(() => {
    const onInvalidate = () => setBumpKey(k => k + 1)
    _subscribers.push(onInvalidate)
    return () => {
      _subscribers = _subscribers.filter(fn => fn !== onInvalidate)
    }
  }, [])

  useEffect(() => {
    if (!user?.uid) {
      setState({ orgs: [], accountType: 'personal', staffQueryFailed: false })
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    loadUserOrgs(user)
      .then(result => { if (!cancelled) setState(result) })
      .catch(err => { console.warn('[userOrgs] hook load failed:', err) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [user?.uid, bumpKey])

  return { ...state, loading }
}
